'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';

interface DataItem {
  id: string;
  type: string;
  correct: number;
  total: number;
  time: number;
}

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; name: string }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  const pct = payload[0].value as number;
  return (
    <div className="bg-card border border-border rounded-lg shadow-elevated p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`font-bold tabular-nums ${pct >= 80 ? 'text-accent' : pct >= 60 ? 'text-warning' : 'text-danger'}`}>{pct}% accuracy</p>
      <p className="text-muted-foreground mt-0.5">Band 8+ threshold: 85%</p>
    </div>
  );
};

export default function QuestionTypeAccuracyChart({ data }: { data: DataItem[] }) {
  const chartData = data.map((d) => ({
    id: d.id,
    type: d.type.length > 12 ? d.type.slice(0, 11) + '…' : d.type,
    accuracy: Math.round((d.correct / d.total) * 100),
  }));

  return (
    <div className="card-base p-5">
      <h2 className="section-header mb-1">Accuracy by Question Type</h2>
      <p className="text-xs text-muted-foreground mb-4">Mock Exam 1 · dashed line = Band 8+ threshold (85%)</p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="type"
            tick={{ fontSize: 9, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <ReferenceLine
            y={85}
            stroke="var(--accent)"
            strokeDasharray="4 3"
            strokeWidth={1.5}
            label={{ value: '85% target', position: 'insideTopRight', fontSize: 10, fill: 'var(--accent)' }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.5 }} />
          <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
            {chartData.map((entry) => (
              <Cell
                key={entry.id}
                fill={entry.accuracy >= 80 ? 'var(--accent)' : entry.accuracy >= 60 ? 'var(--warning)' : 'var(--danger)'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}